/**
 * NFC Solutions — Session 40: offline-safe stock actions.
 *
 * Every stock-affecting action (void, waste, receiving, adjustment,
 * transfer, stock take) goes through `performStockAction`:
 *
 *   1. A client `change_id` is minted FIRST and sent with the HTTP call.
 *   2. Online + 2xx  → done (server stored the change_id, so any later
 *      replay of the same id is answered `duplicate`, never re-applied).
 *   3. Offline, timeout, network error or 5xx → the SAME payload is
 *      persisted to the IndexedDB `event_queue` (survives restart) with
 *      the SAME change_id via `localWrite`; the existing sync loop drains
 *      it to /api/events/sync when the connection returns. If the HTTP
 *      call actually reached the server before the timeout, the replay
 *      is a duplicate — no double deduction.
 *   4. A 4xx is a genuine validation problem → surfaced to the caller,
 *      nothing is queued.
 *
 * The device never sends a final quantity to overwrite the server; only
 * the event. The server classifies consumption (normal / fallback /
 * shortfall) from its own balances at replay time.
 */
import { localWrite, listQueuedEvents, markEventStatus } from "./eventEngine";
import { EventLifecycle, STOCK_EVENT_TYPES, uuidV4 } from "./events";
import { deleteOne } from "./localDb";

export const STOCK_ACTION_QUEUED = "nfcs:stock-action-queued";

export function newChangeId(prefix = "stk") {
  return `${prefix}-${uuidV4()}`;
}

function _isOffline() {
  try { return typeof navigator !== "undefined" && navigator.onLine === false; } catch (_) { return false; }
}

function _shouldQueue(err) {
  if (!err) return false;
  if (err.code === "ECONNABORTED" || err.code === "ERR_NETWORK") return true;
  const status = err.response && err.response.status;
  if (!status) return true;           // no response at all → network
  return status >= 500;               // server unavailable
}

/**
 * @param {object} opts
 *   http, authHeaders          — axios instance + headers for the online path
 *   method, path               — HTTP call ("post", "/owner/inventory/waste")
 *   body                       — request body (change_id is added)
 *   eventType, eventPayload    — queued form (defaults to `body`)
 *   session                    — role session (for envelope metadata)
 *   parentChangeId             — e.g. the order_created change_id for a void
 *   changeId                   — reuse a specific id (retry of a queued row)
 * @returns {{ status: "applied"|"queued", data?: object, change_id: string, duplicate?: boolean }}
 */
export async function performStockAction({ http, authHeaders, method = "post", path, body = {}, eventType, eventPayload, session, parentChangeId, changeId, timeoutMs = 12000 }) {
  const change_id = changeId || newChangeId();
  const wireBody = { ...body, change_id };
  const queue = async (reason) => {
    const res = await localWrite({
      type: eventType,
      payload: { ...(eventPayload || body), change_id },
      session: session || null,
      parentChangeId: parentChangeId || null,
      changeId: change_id,
    });
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(STOCK_ACTION_QUEUED, { detail: { change_id, event_type: eventType, reason } }));
      }
    } catch (_) { /* noop */ }
    return { status: "queued", change_id: res.change_id || change_id, reason };
  };
  if (_isOffline()) return queue("offline");
  try {
    const r = await http.request({ method, url: path, data: wireBody, headers: authHeaders, timeout: timeoutMs });
    return { status: "applied", data: r.data, change_id, duplicate: !!(r.data && r.data.duplicate) };
  } catch (err) {
    if (_shouldQueue(err)) return queue(err.code || (err.response && `http_${err.response.status}`) || "network");
    throw err;
  }
}

/** Queued / synced / failed stock events on THIS device (IndexedDB). */
export async function listStockQueue() {
  const rows = await listQueuedEvents();
  const stock = rows.filter((r) => STOCK_EVENT_TYPES.includes(String(r.event_type || r.type || "")) || String(r.event_type || "") === "order_created");
  const status = (r) => String((r._meta && r._meta.status) || "");
  return {
    rows: stock.sort((a, b) => String(b.ts || "").localeCompare(String(a.ts || ""))),
    counts: {
      local: stock.filter((r) => status(r) === EventLifecycle.LOCAL).length,
      pending: stock.filter((r) => status(r) === EventLifecycle.PENDING).length,
      synced: stock.filter((r) => status(r) === EventLifecycle.SYNCED).length,
      failed: stock.filter((r) => status(r) === EventLifecycle.FAILED).length,
    },
  };
}

/** Put a FAILED row back in the queue (after the manager fixed the cause). */
export async function retryQueuedEvent(changeId) {
  await markEventStatus(changeId, { status: EventLifecycle.LOCAL, _meta: { retried_at: new Date().toISOString(), server_error: null } });
}

/** Drop a row from the local queue (manager decision — audited server-side by the caller). */
export async function discardQueuedEvent(changeId) {
  try { await deleteOne("event_queue", changeId); } catch (_) { /* noop */ }
}
